import React, { memo, useCallback, useContext, useEffect, useMemo } from "react";

// components
import { ErrorBoundary } from "@swiggy-private/core-ui";
// nr events
import {
    FlowType,
    OneMembershipNudge,
    OneMemberShipNudgeProps,
    SWIGGY_ONE_COMPONENT,
    SWIGGY_ONE_STATE,
    getErrorBucket,
    recordSwiggyOneNREvent,
    CART_UPSELL_TEST_IDS,
    recordSwiggyOneNREventDataTestIdVisibility,
} from "@swiggy-private/super-ui";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useModalVisibility } from "@cart-client/hooks/useModalVisibility";
import { useGratificationPopupAnalytics } from "@cart-client/hooks/useGratificationPopupAnalytics";
import { useOneMembershipNudgeAnalytics } from "@cart-client/hooks/useOneMembershipNudgeAnalytics";
import { useOneMembershipPlanHandlers } from "@cart-client/hooks/useOneMembershipPlanHandlers";

// helpers
import { getNestedValue, isEmpty } from "@swiggy-private/js-utils";
import { getUserSuperStatus, navigateToDeeplinkGenericHelper } from "@cart-client/helpers/Cart";

// constants
import { GRATIFICATION_USECASES, PLAN_ADDITION_SOURCE } from "@cart-client/constants/index";
import { ONE_MEMBERSHIP_GRATIFICATION_SID } from "@cart-client/constants/StorageKeys";

// types
import { PresentationInfo, UserSuperStatus } from "@swiggy-private/cart-types";

const OneMemberShipNudgeWrapper: React.FC = () => {
    const { cartState } = useContext(CartContext);
    const { isAnyModalVisible } = useModalVisibility();

    const { addSuperToCart, removeSuperFromCart } = useOneMembershipPlanHandlers();

    const presentationInfo = useMemo(
        () => getNestedValue(cartState, "reviewedCart.data.swiggyOneInfo.presentationInfo", {} as PresentationInfo),
        [cartState],
    );
    const itemNudgeComms = useMemo(() => presentationInfo?.item_nudge_comms || null, [presentationInfo]);
    const isComparisonNudge = useMemo(() => !isEmpty(presentationInfo?.comparison_nudge_comms), [presentationInfo]);
    const gratificationComms = useMemo(
        () => presentationInfo?.swiggy_one_nudge_gratification_card_comms || null,
        [presentationInfo],
    );
    const planDetails = useMemo(() => presentationInfo?.plan_details, [presentationInfo]);
    const removePlanMessage = useMemo(() => presentationInfo?.remove_plan_message, [presentationInfo]);

    useEffect(() => {
        recordSwiggyOneNREventDataTestIdVisibility(SWIGGY_ONE_COMPONENT.ITEM_NUDGE, CART_UPSELL_TEST_IDS.NUDGE);
        if (isEmpty(itemNudgeComms)) {
            recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.ITEM_NUDGE, SWIGGY_ONE_STATE.NO_COMMS);
        } else if (isAnyModalVisible) {
            recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.ITEM_NUDGE, SWIGGY_ONE_STATE.MODAL_BLOCKING);
        } else {
            recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.ITEM_NUDGE, SWIGGY_ONE_STATE.MOUNTED, {
                planId: String(planDetails?.plan_id || ""),
                planType: planDetails?.type || "",
            });
        }
    }, [itemNudgeComms, isAnyModalVisible, planDetails]);

    const addSuperCallback = useCallback(
        (planId: string, planType?: string) => {
            addSuperToCart(planId, FlowType.LINE_ITEM, planType);
        },
        [addSuperToCart],
    );
    const removeSuperCallback = useCallback(() => {
        removeSuperFromCart(true);
    }, [removeSuperFromCart]);

    const userSuperStatus = useMemo(() => getUserSuperStatus(cartState.reviewedCart) as UserSuperStatus, [cartState]);

    // analytics events
    const nudgeAnalytics = useOneMembershipNudgeAnalytics({
        itemNudgeComms,
        planDetails,
        source: isComparisonNudge
            ? PLAN_ADDITION_SOURCE.SUBSCRIPTION_RENEWAL_NUDGE
            : PLAN_ADDITION_SOURCE.CART_UPSELL_NUDGE,
        userSuperStatus,
    });
    const gratificationAnalytics = useGratificationPopupAnalytics(
        gratificationComms,
        PLAN_ADDITION_SOURCE.CART_UPSELL_NUDGE,
        GRATIFICATION_USECASES.BENIFITS,
        planDetails,
    );

    // Props to be passed to the OneMembershipNudge component
    const props = useMemo(
        () =>
            ({
                itemNudgeComms,
                gratificationComms,
                planDetails,
                removePlanMessage,
                handlers: {
                    navigateToDeeplinkGenericHelper,
                    addSuperToCart: addSuperCallback,
                    removeSuperFromCart: removeSuperCallback,
                },
                config: { sessionKey: ONE_MEMBERSHIP_GRATIFICATION_SID, isAnyModalVisible },
                analyticsEvents: { nudgeAnalytics, gratificationAnalytics },
            }) as OneMemberShipNudgeProps,
        [
            addSuperCallback,
            gratificationAnalytics,
            gratificationComms,
            isAnyModalVisible,
            itemNudgeComms,
            nudgeAnalytics,
            planDetails,
            removePlanMessage,
            removeSuperCallback,
        ],
    );

    const onItemNudgeError = useCallback((error?: Error) => {
        recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.ITEM_NUDGE, SWIGGY_ONE_STATE.ERROR_BOUNDARY, {
            errorName: error?.name || "",
            errorBucket: getErrorBucket(error),
        });
    }, []);

    return (
        <ErrorBoundary errorFallback={onItemNudgeError}>
            <OneMembershipNudge {...props} isCartV2 />
        </ErrorBoundary>
    );
};

export default memo(OneMemberShipNudgeWrapper);
